/**
 * @file ResponsiveListTable.tsx
 * @description Табличная раскладка `ResponsiveList` (от 1024; phase19.31,
 * шаг О4; commora-console.md §9).
 *
 * Построена на `ui/table`: шапка 44 обычным регистром, строка 52 (56 у
 * двухстрочной), наведение, линии — всё из темы. Строка-ссылка целиком:
 * нажатие в любом месте строки открывает запись, а для клавиатуры и
 * программ чтения экрана в первой ячейке стоит настоящая ссылка. «⋯» —
 * последним столбцом, компактная кнопка 32px.
 *
 * Ширина (дефект приёмки 2026-10-02, журнал сборки на 1024: таблица 1196px
 * в блоке 696px, «Позиций» и «Недостач» за краем без подсказки):
 *  - на 1024–1279 колонки с `fold` сворачиваются второй линией в соседнюю
 *    ячейку, минимальная ширина таблицы не действует — таблица влезает в блок;
 *  - с 1280 — все столбцы и минимальная ширина `minWidth`;
 *  - если таблица всё же шире блока, она прокручивается внутри рамки, и край
 *    гаснет (`useScrollFade`) — видно, что дальше есть ещё столбцы.
 */

import { useNavigate, Link } from "react-router";
import type { MouseEvent, ReactNode } from "react";

import { LIST_FRAME_CLASS } from "./ListStates";
import type { ListColumn } from "./responsive-list-types";
import { RowActionsMenu, type RowAction } from "./RowActionsMenu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./table";
import { useScrollFade } from "./useScrollFade";
import { WIDE_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

export interface ListTableProps<T> {
  rows: T[];
  columns: ListColumn<T>[];
  rowKey: (row: T) => string;
  rowHref?: (row: T) => string;
  rowLabel?: (row: T) => string;
  rowActions?: (row: T) => RowAction[];
  rowActionsTitle?: (row: T) => string;
  rowSize: "default" | "double";
  ariaLabel: string;
  /** Минимальная ширина таблицы с 1280 (класс): ниже колонки сворачиваются. */
  minWidth?: string;
  /** «⋯» строки: `compact` — 32 с 1024 (по теме), `default` — 48 (SC44; журнал «Не найдено»). */
  actionsSize?: "compact" | "default";
}

/** Клик по строке не должен срабатывать, когда человек выделяет текст. */
function hasSelection(): boolean {
  return (window.getSelection?.()?.toString().length ?? 0) > 0;
}

/** Какие колонки — столбцы, а какие свёрнуты второй линией (по ширине экрана). */
function splitTableColumns<T>(all: ListColumn<T>[], wide: boolean) {
  const table = all.filter((col) => !col.compactOnly);
  if (wide) return { visible: table, folded: [] as ListColumn<T>[] };
  return { visible: table.filter((col) => !col.fold), folded: table.filter((col) => col.fold) };
}

/** Ячейка столбца + линии свёрнутых в неё колонок. */
function cellContent<T>(col: ListColumn<T>, row: T, folded: ListColumn<T>[]): ReactNode {
  const extra = folded.filter((f) => f.fold?.into === col.key);
  if (extra.length === 0) return col.cell(row);
  return (
    <span className="flex min-w-0 flex-col">
      <span className="min-w-0">{col.cell(row)}</span>
      {extra.map((f) => (
        <span key={f.key} className="type-caption truncate text-on-surface-variant">
          {(f.fold?.cell ?? f.cell)(row)}
        </span>
      ))}
    </span>
  );
}

export function ResponsiveListTable<T>({
  rows,
  columns: allColumns,
  rowKey,
  rowHref,
  rowLabel,
  rowActions,
  rowActionsTitle,
  rowSize,
  ariaLabel,
  minWidth,
  actionsSize = "compact",
}: ListTableProps<T>) {
  const navigate = useNavigate();
  const wide = useMediaQuery(WIDE_QUERY);
  const fade = useScrollFade<HTMLDivElement>();
  const withActions = !!rowActions;
  const { visible: columns, folded } = splitTableColumns(allColumns, wide);

  return (
    <div className={LIST_FRAME_CLASS}>
      <Table
        aria-label={ariaLabel}
        data-row-size={rowSize}
        className={cn("table-fixed", wide && minWidth)}
        containerProps={{ ref: fade.ref, onScroll: fade.update, style: fade.style }}
      >
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} className={col.width} />
          ))}
          {withActions ? <col className="w-14" /> : null}
        </colgroup>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} aria-sort={col.ariaSort} className={cn("whitespace-nowrap", col.align === "end" && "text-right")}>
                {col.headerNode ?? col.header}
              </TableHead>
            ))}
            {withActions ? (
              <TableHead>
                <span className="sr-only">Действия</span>
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const href = rowHref?.(row);
            const actions = rowActions?.(row) ?? [];
            const open = href
              ? (event: MouseEvent) => {
                  // Ссылка и «⋯» обрабатывают нажатие сами — строка не дублирует переход.
                  if ((event.target as HTMLElement).closest("a,button,[role='menuitem']")) return;
                  if (!hasSelection()) navigate(href);
                }
              : undefined;
            return (
              <TableRow key={rowKey(row)} onClick={open} className={href ? "cursor-pointer" : undefined}>
                {columns.map((col, index) => (
                  <TableCell key={col.key} className={cn("py-2", col.align === "end" && "text-right")}>
                    {index === 0 && href ? (
                      <Link to={href} aria-label={rowLabel?.(row)} className="block min-w-0 outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                        {cellContent(col, row, folded)}
                      </Link>
                    ) : (
                      cellContent(col, row, folded)
                    )}
                  </TableCell>
                ))}
                {withActions ? (
                  <TableCell className="py-2">
                    <div className="flex justify-end">
                      <RowActionsMenu
                        actions={actions}
                        size={actionsSize}
                        title={rowActionsTitle?.(row) ?? "Действия"}
                        ariaLabel={rowActionsTitle?.(row) ?? "Действия"}
                      />
                    </div>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
